import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AsyncBoundary } from '@/components/AsyncBoundary/AsyncBoundary';
import { Badge } from '@/components/Badge/Badge';
import { Button } from '@/components/Button/Button';
import { Card } from '@/components/Card/Card';
import { DataTable, type Column } from '@/components/DataTable/DataTable';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import { TextField } from '@/components/Field/Field';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { PresenterControls } from '@/features/admin/pages/PresenterControls';
import { api } from '@/lib/api/endpoints';
import type { AccessGrant } from '@/lib/api/types';
import { formatDateTime } from '@/lib/format';
import { useAsync } from '@/lib/hooks/useAsync';
import { useSubmit } from '@/lib/hooks/useSubmit';
import styles from './pages.module.scss';

/** Everything scoped to one cohort: attendees (Epic 3) and live control (Epic 4). */
export function CohortDetailPage() {
  const { cohortId = '' } = useParams();
  const { state, reload } = useAsync(() => api.grants.list(cohortId), [cohortId]);

  return (
    <div className={styles.stack}>
      <PageHeader
        eyebrow="Cohort"
        title={cohortId}
        description={<Link to="/admin/cohorts">← Back to all cohorts</Link>}
      />

      <PresenterControls cohortId={cohortId} />

      <GrantAccessForm cohortId={cohortId} onGranted={reload} />

      <Card title="Attendees (US-3.4)" footer="This list never exposes another cohort's membership.">
        <AsyncBoundary state={state} subject="attendees" plannedUnder="US-3.4" onRetry={reload}>
          {({ grants }) => <GrantTable cohortId={cohortId} grants={grants} onChanged={reload} />}
        </AsyncBoundary>
      </Card>
    </div>
  );
}

function grantStatus(grant: AccessGrant): { tone: 'positive' | 'critical' | 'warning'; label: string } {
  if (grant.revoked_at) return { tone: 'critical', label: 'revoked' };
  const now = Date.now();
  if (new Date(grant.expires_at).valueOf() <= now) return { tone: 'warning', label: 'expired' };
  if (new Date(grant.starts_at).valueOf() > now) return { tone: 'warning', label: 'pending' };
  return { tone: 'positive', label: 'active' };
}

function GrantTable({
  cohortId,
  grants,
  onChanged,
}: {
  cohortId: string;
  grants: readonly AccessGrant[];
  onChanged: () => void;
}) {
  const { submit } = useSubmit(
    (input: { grantId: string; reason: string }) =>
      api.grants.revoke(cohortId, input.grantId, input.reason),
    onChanged,
  );

  // US-3.4: revocation is explicit and records a reason.
  const revoke = (grant: AccessGrant) => {
    const reason = window.prompt(`Revoke access for ${grant.attendee_identity}? Reason:`);
    if (reason && reason.trim()) void submit({ grantId: grant.id, reason: reason.trim() });
  };

  const columns: ReadonlyArray<Column<AccessGrant>> = [
    { key: 'identity', header: 'Attendee', render: (grant) => grant.attendee_identity },
    {
      key: 'status',
      header: 'Access',
      render: (grant) => {
        const status = grantStatus(grant);
        return <Badge tone={status.tone}>{status.label}</Badge>;
      },
    },
    { key: 'starts', header: 'Starts', secondary: true, render: (grant) => formatDateTime(grant.starts_at) },
    { key: 'expires', header: 'Expires', render: (grant) => formatDateTime(grant.expires_at) },
    {
      key: 'seen',
      header: 'Last activity',
      secondary: true,
      render: (grant) => formatDateTime(grant.last_seen_at),
    },
    {
      key: 'actions',
      header: '',
      render: (grant) =>
        grant.revoked_at ? (
          <span className={styles.note}>{grant.revoked_reason ?? 'revoked'}</span>
        ) : (
          <Button size="sm" variant="danger" onClick={() => revoke(grant)}>
            Revoke
          </Button>
        ),
    },
  ];

  return (
    <DataTable
      caption="Attendee access grants"
      columns={columns}
      rows={grants}
      rowKey={(grant) => grant.id}
      empty={<EmptyState title="No attendees yet" description="Grant access to add the first attendee." />}
    />
  );
}

/** US-3.3 — time-bound access for one attendee identity. */
function GrantAccessForm({ cohortId, onGranted }: { cohortId: string; onGranted: () => void }) {
  const [identity, setIdentity] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const { submit, busy, message } = useSubmit(
    (input: { attendeeIdentity: string; startsAt: string; expiresAt: string }) =>
      api.grants.create(cohortId, input),
    () => {
      setIdentity('');
      onGranted();
    },
  );

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    void submit({ attendeeIdentity: identity.trim(), startsAt, expiresAt });
  };

  return (
    <Card title="Grant attendee access (US-3.3)">
      <form onSubmit={onSubmit}>
        <div className={styles.formGrid}>
          <TextField
            label="Attendee identity"
            required
            placeholder="attendee@example.com"
            hint="The identity recognised by the login provider."
            value={identity}
            onChange={(event) => setIdentity(event.target.value)}
          />
          <TextField
            label="Access starts"
            type="datetime-local"
            required
            value={startsAt}
            onChange={(event) => setStartsAt(event.target.value)}
          />
          <TextField
            label="Access expires"
            type="datetime-local"
            required
            hint="Enforced server-side on every request, not just at login."
            value={expiresAt}
            onChange={(event) => setExpiresAt(event.target.value)}
          />
        </div>
        <div className={styles.formActions}>
          {message && <span className={styles.note}>{message}</span>}
          <Button type="submit" variant="primary" disabled={busy || !identity}>
            {busy ? 'Granting…' : 'Grant access'}
          </Button>
        </div>
      </form>
    </Card>
  );
}
